/**
 * AuthContext — React context for authentication state
 *
 * Provides:
 * - user: the current authenticated user's profile (from sam-dbam)
 * - loading: whether the auth check is still in progress
 * - error: any error from the auth check
 * - logout: function to sign out
 * - refreshUser: function to re-fetch user profile
 *
 * Uses Hanko frontend SDK for session management.
 *
 * Usage:
 *   import { AuthProvider, useAuth } from './context/AuthContext';
 *
 *   // In App.js:
 *   <AuthProvider><App /></AuthProvider>
 *
 *   // In any component:
 *   const { user, loading, logout } = useAuth();
 */

import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { END_REASON_SIGNOUT } from '@cloudsam/ui';
import hanko from '../hanko-init';
import { resetArchiveStateOnLogout } from '../utils/archiveApi';
import { idleClock, draftStore } from '../idle-session';
import { closeHelpAgent } from '../utils/openHelpAgent';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // True once the idle clock has been started for the session this page
    // load is holding. Reset when a check finds nobody signed in, so a
    // sign-in later in the same page load starts the clock again.
    const sessionStartedRef = useRef(false);

    /**
     * Check if the user has an active Hanko session
     * and fetch their profile from the Console backend.
     */
    const checkAuth = useCallback(async () => {
        try {
            setError(null);
            const sessionValid = hanko.session.isValid();

            if (!sessionValid) {
                sessionStartedRef.current = false;
                setUser(null);
                setLoading(false);
                return;
            }

            // Session exists — fetch user profile from Console backend
            const response = await fetch('/api/console/me', {
                credentials: 'include',
            });

            if (response.ok) {
                const userData = await response.json();
                console.log('[AuthContext] /api/console/me response:', JSON.stringify({ user_id: userData?.user_id, email: userData?.email, org_id: userData?.org_id, synced: userData?.synced }));
                setUser(userData);
                // Start the idle clock for this sign-in.
                //
                // This hangs on the session check, not on the Hanko SDK's
                // onSessionCreated event. Both sign-in screens drive the Flow API
                // and call back from its own `success` handler, so that event is not
                // a reliable signal. Relying on it left the end-of-session cookie in
                // place, and every tab opened afterwards locked itself against a
                // session that had just been proved good.
                //
                // It fires on the change from "no session" to "session", so a fresh
                // page load does it once and a repeat check does not touch the clock.
                if (!sessionStartedRef.current) {
                    sessionStartedRef.current = true;
                    idleClock.startSession();
                }
                // Two people can share a machine. Any recovery draft left by
                // somebody else must go before this person can see the app.
                try {
                    draftStore.clearDraftsForOtherUsers(userData?.user_id || '');
                } catch (err) {
                    console.warn('[AuthContext] could not clear other accounts drafts');
                }
            } else if (response.status === 401) {
                // Session expired or invalid
                sessionStartedRef.current = false;
                setUser(null);
            } else {
                console.error('[AuthContext] Failed to fetch user profile:', response.status);
                // Still set user to null but keep the session —
                // sam-dbam might be temporarily unavailable
                setUser(null);
            }
        } catch (err) {
            console.error('[AuthContext] Auth check error:', err);
            setError(err);
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        checkAuth();

        // Listen for session expiry
        hanko.onSessionExpired(() => {
            sessionStartedRef.current = false;
            setUser(null);
        });

        hanko.onUserLoggedOut(() => {
            // The idle-timeout logout ends the session this way, not through
            // checkAuth(). Without this reset, a sign-in later in the same
            // page load finds sessionStartedRef still true, skips
            // idleClock.startSession(), and leaves the "session ended"
            // cookie in place — so IdleSessionGate remounts straight into
            // the lock screen even though the new sign-in is good. A full
            // reload masked this, because it starts the ref over from
            // scratch; clicking "Go to sign in" was the reload.
            sessionStartedRef.current = false;
            setUser(null);
        });

        hanko.onSessionCreated(() => {
            // Clear the "this session has ended" cookie and restart the idle
            // clock. Without this, somebody who signs back in is locked out
            // again on the next tick by the cookie left from the sign-out
            // before.
            idleClock.startSession();
            checkAuth();
        });
    }, [checkAuth]);

    /**
     * Sign out: revoke Hanko session and clear user state.
     */
    const logout = useCallback(async () => {
        // Tell the other tabs first, before any network call. Designer,
        // Explorer and Channels cannot be closed from here, so they watch a
        // shared cookie instead and lock themselves when it appears. Writing it
        // first means they lock straight away rather than waiting on Hanko.
        idleClock.endSession(END_REASON_SIGNOUT);
        // The help agent popup is the one window we can close, because we kept
        // the reference that opened it.
        closeHelpAgent();
        // Clear the archive definition first, while the org context is still
        // present so the current account's scoped keys resolve and get dropped.
        // This prevents one account's active archive file from carrying over to
        // the next account that logs in on this browser.
        try {
            await resetArchiveStateOnLogout();
        } catch (err) {
            console.error('[AuthContext] Archive state reset error:', err);
        }
        // Tell the server the session is ending, while it is still valid.
        // Hanko does not report a sign-out to CloudSAM. Without this call the
        // stored session expiry stays in the future, so the SuperPanel Monitor
        // keeps listing this person as signed in. Never let it block sign-out.
        try {
            await fetch('/api/console/signout', { method: 'POST', credentials: 'include' });
        } catch (err) {
            console.warn('[AuthContext] sign-out could not be recorded');
        }
        try {
            await hanko.user.logout();
            setUser(null);
        } catch (err) {
            console.error('[AuthContext] Logout error:', err);
            // Clear local state even if the API call fails
            setUser(null);
        }
    }, []);

    /**
     * Re-fetch the user profile (e.g. after profile update).
     */
    const refreshUser = useCallback(async () => {
        await checkAuth();
    }, [checkAuth]);

    const value = {
        user,
        loading,
        error,
        logout,
        refreshUser,
        isAuthenticated: !!user,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

/**
 * Hook to access auth context.
 * Must be used within an AuthProvider.
 */
export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}

export default AuthContext;
